import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import { addressesIncludeKey, phoneNumberIncludeKey } from '@/_containers';
import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type {
  CompetitorCreateSchema,
  CompetitorQueryLinkerSchema,
  CompetitorQuerySchema,
  CompetitorsQueryAllSchema,
  CompetitorUpdateSchema,
} from './schemas';

const includeKeys = [
  phoneNumberIncludeKey('current_web_clients'),
  'current_web_clients.logo',
  'associated_clients.logo',
  phoneNumberIncludeKey('associated_clients'),
  'associated_clientsCount',
  'logo',
  addressesIncludeKey(),
  phoneNumberIncludeKey('primary_contact'),
  'primary_contact.profile_image',
  phoneNumberIncludeKey(),
];

export function getCompetitorsQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['COMPETITORS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<CompetitorsQueryAllSchema>({
        endpoint: '/api/v1/competitors',
        params: `${apiParamStr}&include=logo,${addressesIncludeKey()},current_web_clientsCount`,
      });

      return res;
    },
  };
}

export function useGetCompetitors(apiParamStr: string) {
  return useSuspenseQuery(getCompetitorsQuery(apiParamStr));
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['COMPETITORS'] });
};

export const useGetCompetitorsMutation: UseControlledMutation<CompetitorQueryLinkerSchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['COMPETITORS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<CompetitorsQueryAllSchema>({
            endpoint: '/api/v1/competitors',
            params: { search, per_page, include: 'logo' },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetCompetitor(id: string) {
  return useSuspenseQuery(getCompetitorQuery(id));
}

export function getCompetitorQuery(id: string) {
  return {
    queryKey: ['COMPETITOR', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: CompetitorQuerySchema }>({
        endpoint: `/api/v1/competitors/${id}`,
        params: {
          include: includeKeys.join(','), // `${phoneNumberIncludeKey('current_web_clients')},current_web_clients.profile_image,associated_clients,associated_clients.logo,${phoneNumberIncludeKey('associated_clients')},associated_clientsCount,logo,${addressesIncludeKey()},primary_contact,${phoneNumberIncludeKey('primary_contact')},primary_contact.profile_image,${phoneNumberIncludeKey()}`,
        },
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const invalidateCompetitorQuery = (id: string) => {
  queryClient.invalidateQueries({ queryKey: ['COMPETITOR', id] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, unknown, CompetitorCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/competitors/',
        payload,
      }),
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: CompetitorQuerySchema }, ApiError, CompetitorUpdateSchema>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/competitors/${id || bId}`,
        method: 'PATCH',
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['AUDIT_REVISION_HISTORY', 'competitors', id] });
    },
  });

export const usePatchMutation = () =>
  useMutation<{ data: CompetitorQuerySchema }, ApiError, Partial<CompetitorUpdateSchema>>({
    mutationFn: ({ id, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/competitors/${id}`,
        method: 'PATCH',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['COMPETITOR_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/competitors/${id}`,
        method: 'DELETE',
      }),
  });
